import { memo } from 'react';
import type { ModelProps } from './parts';
import { buildingModelFor, footprintModelFor, unitModelFor } from './registry';

/**
 * Low-poly unit and building models built from primitives. Each model sits on
 * y = 0 (the tile surface), faces +z, and takes a player-coloured `accent`
 * (flat) and `glow` (emissive) material plus its `level`; everything else
 * uses the shared palettes in `materials.ts`. Which component draws a type is
 * set in `registry.ts`; the city's look per level in `city/stages.tsx`.
 */

export { AnimateContext, Animated, Bob, Pulse, Spin } from './animated';
export { FenceSection } from './buildings/fence';
export { WatchSpotMarker } from './buildings/watchtower';
export {
  BURST_GEOMETRY,
  BURST_MATERIAL,
  burstScale,
  CLOD_GEOMETRY,
  CLOD_LIFE,
  CLOD_MATERIAL,
  clodHeight,
  DIRT_CLODS,
  DUST_GEOMETRY,
  DUST_LIFE,
  DUST_MATERIAL,
  dustScale,
  FLASH_COLOR,
  FLASH_GEOMETRY,
  FLASH_MATERIAL,
  fade,
  fadeColor,
  flashScale,
  HIT_BURST_LIFE,
  HIT_FLASH_LIFE,
  type HitVisual,
  IMPACT_VISUALS,
  type ImpactVisual,
  type MissVisual,
  SPARK_COLORS,
  SPARK_GEOMETRY,
  SPARK_LIFE,
  SPARK_MATERIAL,
  SPARK_WIDTH,
  sparkAt,
  sparkDirection,
  TARGET_FLASH,
  TARGET_FLASH_GEOMETRY,
  TARGET_FLASH_MATERIAL,
} from './impacts';
export { glowMaterial } from './materials';
export type { ModelProps } from './parts';
export { BUILDING_MODELS, UNIT_MODELS } from './registry';
export {
  SPRAWL_VARIANTS,
  type SprawlProps,
  SprawlTileModel,
} from './sprawl';
export { SPY_MARKERS } from './units/covert';
export {
  DEFAULT_UNIT_MUZZLE,
  HEAL_COLOR,
  type HealKind,
  type HealVisual,
  healVisualFor,
  type Muzzle,
  muzzleFor,
  REPAIR_COLOR,
  type ShooterVisual,
  type ShotKind,
} from './visuals';

/** Units are modelled at roughly real proportions, then enlarged to read at map zoom. */
const UNIT_SCALE = 1.35;

// The models are memoised: their props (type, shared materials, level) rarely
// change, so a tick re-rendering a layer doesn't walk every model's tree.

export const UnitModel = memo(function UnitModel({
  type,
  ...props
}: ModelProps & { type: string }) {
  const Model = unitModelFor(type);
  return (
    <group scale={UNIT_SCALE}>
      <Model {...props} />
    </group>
  );
});

export const BuildingModel = memo(function BuildingModel({
  type,
  ...props
}: ModelProps & { type: string }) {
  const Model = buildingModelFor(type);
  return <Model {...props} />;
});

/** What covers an extra tile of a grown building (farm crop rows, forestry grove, barracks yard, mine spoil heaps). */
export const FootprintTileModel = memo(function FootprintTileModel({
  type,
  ...props
}: ModelProps & { type: string }) {
  const Model = footprintModelFor(type);
  return <Model {...props} />;
});
